import Image from "next/image";
import messages from "@/messages/en.json";
import type { BeerCardProps } from "./beer-card.interface";

const t = messages.whatsOnTap.beerCard;

function badgeLabel(badge: string): string {
  const map: Record<string, string> = {
    "just-added": t.badge.justAdded,
    "selling-fast": t.badge.sellingFast,
    "coming-soon": t.badge.comingSoon,
  };
  return map[badge] ?? badge;
}

export function BeerCard({ beer, compact = false }: BeerCardProps) {
  const dotColor = beer.colorFrom ?? "#c8a84b";
  const imageAlt = t.imageAlt
    .replace("{name}", beer.name)
    .replace("{brewery}", beer.brewery);

  type StylesPorps = {
    container: string;
    beerStyle: {
      container: string;
    };
    image: {
      container: string;
      image: string;
    };
  };

  const styles: Record<"compact" | "default", StylesPorps> = {
    compact: {
      container: "pt-6 px-6 pb-5.25 xl:pt-6 xl:pb-5.25",
      beerStyle: {
        container: "mb-5.25",
      },
      image: {
        container: "mb-6 px-7.5 xl:px-9",
        image: "max-w-[234px] max-h-[312px] aspect-[234/312]",
      },
    },
    default: {
      container: "pt-6 px-4.5 pb-[15px] xl:pt-10 xl:px-9 xl:pb-7.5",
      beerStyle: {
        container: "",
      },
      image: {
        container: "mb-0 px-[49.5px] xl:px-9",
        image: "max-w-[234px] max-h-[312px] aspect-[234/312]",
      },
    },
  };

  const style = styles[compact ? "compact" : "default"];

  return (
    <div className={`rounded-card flex flex-col bg-white ${style.container}`}>
      {/* Style label + optional badge */}
      <div
        className={`relative flex items-center justify-between gap-2 ${style.beerStyle.container}`}
      >
        <div className="flex h-6.5 items-center gap-2.5">
          <span
            className="h-5 w-5 shrink-0 rounded-full"
            style={{ backgroundColor: dotColor }}
            aria-hidden="true"
          />
          {beer.style && (
            <span className="text-tap-heading text-center font-mono text-sm">
              {beer.style}
            </span>
          )}
        </div>
        {beer.badge && (
          <span
            className={`font-accent text-tag bg-blush-lambic text-hop rounded-sm px-3 py-1 font-normal absolute right-0 hidden 2xl:block`}
            aria-label={badgeLabel(beer.badge)}
          >
            <span className="text-trim-end">{badgeLabel(beer.badge)}</span>
          </span>
        )}
      </div>

      {/* Pump clip image */}
      {beer.imageUrl && (
        <div
          className={`flex items-center justify-center ${style.image.container}`}
        >
          <Image
            src={beer.imageUrl}
            alt={imageAlt}
            width={234}
            height={312}
            unoptimized
            className={`h-full w-full object-cover ${style.image.image}`}
          />
        </div>
      )}

      {/* Brewery (bold) / Beer name */}
      <div className="mb-4.5 text-center xl:mb-6">
        <h3
          className={`font-display text-base leading-snug font-bold text-hop`}
        >
          {beer.name}
        </h3>
        {beer.breweryUrl && !compact ? (
          <a
            href={beer.breweryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-display text-copper-kettle text-trim-start text-base leading-snug font-medium hover:underline"
          >
            {beer.brewery}
          </a>
        ) : (
          <p className="font-display text-hop text-trim-start text-base leading-snug font-medium">
            {beer.brewery}
          </p>
        )}
      </div>

      {compact ? (
        (beer.container || beer.abv) && (
          <p className="text-hop text-tag text-trim-end text-center font-mono font-normal">
            {[beer.container, beer.abv && `${beer.abv}%`]
              .filter(Boolean)
              .join(" / ")}
          </p>
        )
      ) : (
        <div>
          {beer.abv && (
            <>
              <div className="border-hop border-t" />
              <div className="flex items-baseline justify-between py-2 text-sm">
                <span className="text-hop text-tag font-mono font-medium">
                  {t.label.abv}
                </span>
                <span className="text-hop text-mono-sm font-mono font-normal">
                  {beer.abv}%
                </span>
              </div>
            </>
          )}
          {beer.container && (
            <>
              <div className="border-hop border-t" />
              <div className="flex items-baseline justify-between py-2 text-sm">
                <span className="text-hop text-mono-sm font-mono font-medium">
                  {t.label.style}
                </span>
                <span className="text-hop text-mono-sm font-mono font-normal">
                  {beer.container}
                </span>
              </div>
            </>
          )}
          {beer.notes && (
            <>
              <div className="border-hop border-t" />
              <div className="space-y-2 py-2 text-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-hop text-mono-sm font-mono font-medium">
                    {t.label.notes}
                  </span>
                  {(beer.isVegan || beer.isGlutenFree) && (
                    <span className="text-hop text-mono-sm font-mono font-normal">
                      {[
                        beer.isVegan && t.label.vegan,
                        beer.isGlutenFree && t.label.glutenFree,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  )}
                </div>
                <p className="text-hop text-mono-sm font-mono leading-relaxed font-normal">
                  {beer.notes}
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
